import { isCoverImage } from 'lib/media';
import { Asset, Product, ProductImage } from 'lib/product';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Lightbox from 'react-image-lightbox';

const ProductMediaGallery = ({ product }: { product: Product }) => {
  const { assets } = product;
  const coverImages = useMemo(
    (): Asset[] => assets?.filter((asset) => isCoverImage(asset)),
    [assets],
  );

  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const getNextPhotoIndex = useCallback(
    (index: number): number => (index + 1) % coverImages.length,
    [coverImages.length],
  );

  const getPrevPhotoIndex = useCallback(
    (index: number): number =>
      (index + coverImages.length - 1) % coverImages.length,
    [coverImages.length],
  );

  const setNextHandler = useCallback(
    () => setPhotoIndex((prev) => getNextPhotoIndex(prev)),
    [getNextPhotoIndex],
  );

  const setPrevHandler = useCallback(
    () => setPhotoIndex((prev) => getPrevPhotoIndex(prev)),
    [getPrevPhotoIndex],
  );

  const currentPhoto = useMemo(() => coverImages[photoIndex]?.url, [
    coverImages,
    photoIndex,
  ]);

  const nextPhoto = useMemo(
    () => coverImages[getNextPhotoIndex(photoIndex)]?.url,
    [coverImages, getNextPhotoIndex, photoIndex],
  );

  const prevPhoto = useMemo(
    () => coverImages[getPrevPhotoIndex(photoIndex)]?.url,
    [coverImages, getPrevPhotoIndex, photoIndex],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      if (e.key === 'ArrowRight') {
        e.stopPropagation();
        e.stopImmediatePropagation();
        setPhotoIndex((prev) => getNextPhotoIndex(prev));
      } else if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        e.stopImmediatePropagation();
        setPhotoIndex((prev) => getPrevPhotoIndex(prev));
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () =>
      document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, getNextPhotoIndex, getPrevPhotoIndex]);

  if (!assets?.length) {
    return (
      <Image
        src="/assets/images/No_image_available.svg"
        layout="responsive"
        width="100%"
        height={100}
      />
    );
  }

  return (
    <div className="tw-sticky tw-top-4">
      <div className="tw-grid tw-grid-cols-5 tw-gap-3">
        <div className="tw-col-span-1 tw-flex tw-flex-col tw-justify-between tw-gap-3">
          {coverImages.map((asset, index) => (
            <button
              key={`${product.id}-${index}`}
              className={`tw-relative tw-block tw-p-px before:tw-border before:tw-border-primary before:tw-inset-0 before:tw-content-[''] before:tw-absolute before:tw-block after:tw-content-[''] after:tw-block after:tw-absolute after:tw-inset-0 after:tw-transition-all after:tw-duration-300 after:tw-bg-[hsla(0,0%,100%,.4)] after:tw-visible after:tw-opacity-100 ${
                photoIndex === index
                  ? 'before:tw-visible before:tw-opacity-100 after:tw-opacity-0 after:tw-invisible hover:after:tw-opacity-0 hover:after:tw-invisible'
                  : 'before:tw-invisible before:tw-opacity-0 hover:after:tw-invisible hover:after:tw-opacity-0'
              }`}
              onClick={() => setPhotoIndex(index)}
            >
              <ProductImage
                src={asset.url}
                alt={product.name}
                layout="responsive"
                size={100}
              />
            </button>
          ))}
        </div>
        <div className="tw-col-span-4 tw-relative">
          <ProductImage
            src={currentPhoto}
            alt={product.name}
            size={450}
            layout="responsive"
          />
          <button
            className="btn-product-gallery"
            onClick={() => setIsOpen(true)}
          >
            <i className="icon-arrows" />
          </button>
        </div>
      </div>
      {isOpen ? (
        <Lightbox
          mainSrc={currentPhoto}
          nextSrc={nextPhoto}
          prevSrc={prevPhoto}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={setPrevHandler}
          onMoveNextRequest={setNextHandler}
        />
      ) : null}
    </div>
  );
};

export default ProductMediaGallery;
